/**
 * Kanban board view of accounts grouped by configured stage.
 *
 * Columns come from the active Accounts-view config (one column per stage), so
 * both the column count and labels are tenant/portal-configurable. Each stage
 * maps to a canonical `LifecyclePhase`; drag-and-drop transitions go through
 * `onAttemptMove` with that system phase, which the parent (AccountsList) wires
 * to either a direct mutation (adjacent move) or a skip-confirmation modal
 * (legal-but-non-adjacent).
 *
 * Drop targets are highlighted based on each account's
 * `_canTransition.allowedPhases`. Disallowed targets receive no highlight and
 * the drop is silently ignored.
 */

import React from 'react';
import type {LifecyclePhase} from '@api/generated/client';
import {useAccountsViewConfig} from '@common-web/hooks/useAccountsViewConfig';
import {resolveAccountPosition, visibleStages, type AccountsViewStage} from '@common/accountsView';
import {AccountCard, AccountCardData} from './AccountCard';

interface AccountKanbanProps {
  accounts: AccountCardData[];
  /** Server-bucketed totals per stage key (account.stageCounts) — accurate
   *  beyond the loaded pages. Falls back to loaded-item counts when absent. */
  stageCounts?: Record<string, number>;
  routePrefix: string;
  onAttemptMove: (
    accountsId: string,
    fromPhase: LifecyclePhase,
    toPhase: LifecyclePhase,
  ) => void;
  /** Internal admin may reorder accounts between same-phase stage columns. */
  canReorderWithinPhase?: boolean;
  /** Set an account's sub-stage by the target column's view sub-stage key. */
  onAttemptSubStageMove?: (accountsId: string, viewSubStageKey: string) => void;
}

export function AccountKanban({
  accounts,
  stageCounts,
  routePrefix,
  onAttemptMove,
  canReorderWithinPhase = false,
  onAttemptSubStageMove,
}: AccountKanbanProps): React.ReactElement {
  const config = useAccountsViewConfig();
  const lanes = visibleStages(config);

  // Bucket accounts into stages by their resolved position. Counts are derived
  // from the loaded items so they stay consistent with the configured stages
  // (multiple stages can map to the same system phase).
  const {accountsByStage, stageKeyById} = React.useMemo(() => {
    const byStage: Record<string, AccountCardData[]> = {};
    const keyById: Record<string, string> = {};
    for (const stage of config.stages) byStage[stage.key] = [];
    for (const a of accounts) {
      const pos = resolveAccountPosition(config, a);
      // Null position = the account's phase isn't represented in this view
      // (server filtering should prevent this; skip defensively).
      if (!pos) continue;
      const stageKey = pos.stage.key;
      if (byStage[stageKey]) {
        byStage[stageKey].push(a);
        keyById[a.id] = stageKey;
      }
    }
    return {accountsByStage: byStage, stageKeyById: keyById};
  }, [accounts, config]);

  const [draggingId, setDraggingId] = React.useState<string | null>(null);
  const draggingAccount = accounts.find((a) => a.id === draggingId);
  const draggingStageKey = draggingId ? stageKeyById[draggingId] : undefined;

  const isStageDropTarget = (stage: AccountsViewStage): boolean => {
    if (!draggingAccount) return false;
    // Cross-phase moves: gated by the account's allowed phase transitions.
    if (draggingAccount._canTransition.allowedPhases.includes(stage.systemPhase)) return true;
    // Same-phase reorder (internal admin): a different stage column in the
    // account's current phase sets the sub-stage rather than the phase.
    return (
      canReorderWithinPhase &&
      stage.systemPhase === draggingAccount.lifecyclePhase &&
      stage.key !== draggingStageKey
    );
  };

  return (
    <div className='h-full overflow-x-auto'>
      <div
        className='grid gap-3 h-full'
        style={{
          gridTemplateColumns: `repeat(${lanes.length}, minmax(220px, 1fr))`,
        }}
      >
        {lanes.map((stage) => {
          const isDropTarget = isStageDropTarget(stage);
          const isDraggingFromHere = draggingStageKey === stage.key;
          const blockedReason = draggingAccount?._canTransition.blocked.find(
            (b) => b.phase === stage.systemPhase,
          );
          const items = accountsByStage[stage.key] ?? [];

          return (
            <div
              key={stage.key}
              className={`flex flex-col rounded-md border bg-muted/30 transition-colors ${
                isDropTarget ? 'ring-2 ring-primary bg-primary/5' : ''
              } ${
                draggingAccount && !isDropTarget && !isDraggingFromHere ? 'opacity-60' : ''
              }`}
              title={blockedReason?.tooltip ?? undefined}
              onDragOver={(e) => {
                if (isDropTarget) e.preventDefault();
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (draggingId && isDropTarget && draggingAccount) {
                  if (stage.systemPhase === draggingAccount.lifecyclePhase) {
                    const targetKey = stage.subStages[0]?.key;
                    if (targetKey) onAttemptSubStageMove?.(draggingId, targetKey);
                  } else {
                    onAttemptMove(draggingId, draggingAccount.lifecyclePhase, stage.systemPhase);
                  }
                }
                setDraggingId(null);
              }}
            >
              <div className='px-3 py-2 border-b bg-background flex items-center justify-between'>
                <span className='font-semibold text-sm'>{stage.label}</span>
                <span className='text-xs text-muted-foreground tabular-nums'>
                  {stageCounts?.[stage.key] ?? items.length}
                </span>
              </div>
              <div className='flex-1 overflow-y-auto p-2 space-y-2 min-h-[200px]'>
                {items.length === 0 ? (
                  <div className='text-xs text-muted-foreground text-center py-6'>
                    {isDropTarget ? 'Drop here' : 'No accounts'}
                  </div>
                ) : (
                  items.map((account) => (
                    <AccountCard
                      key={account.id}
                      account={account}
                      routePrefix={routePrefix}
                      onDragStart={() => setDraggingId(account.id)}
                      onDragEnd={() => setDraggingId(null)}
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
